import {
	hotelRoomCategoryResource,
	upsellProductResource,
	checkoutResource,
	type BookingResource,
	type CheckoutResource,
	type HotelRoomCategoryResource,
	type UpsellProductResource,
} from '@/api/resources';
import { defineStore } from 'pinia';
import { isAxiosError, isCancel } from 'axios';
import config from '@/config';
import { isCartNotFoundError } from '@/api/errors';
import { persistResumable, pushSessionExpiry } from '@/stores';
import { useResumableReservation } from '@/composables/useResumableReservation';
import { useCustomerStore } from '@/stores/useCustomerStore';
import { useLoadingStore } from '@/stores/useLoadingStore';
import { useValidationStore } from '@/stores/useValidationStore';
import api from '@/api';
import Cookies from 'js-cookie';
import useTransaction from '@/compositions/useTransaction';
import useCookie from '@/compositions/useCookie';
import bridge from '@thermenresorts/bridge';
import { i18n } from '@/index';
import { parse } from 'date-fns';
import type { FlowStoreState } from '@/stores/schemas';
import { checkoutHasTreatmentOrRitual } from '@/stores/hasTreatmentOrRitual';
import { syncCheckout, upsellsKey } from '@/stores/checkoutResponse';

const formatDate = (dateString: string) => parse(dateString, 'yyyy-MM-dd', new Date());

export type CheckoutOptions = {
	checkout?: string;
	product?: number;
	bath_card_id?: number;
	voucher_codes?: string[];
	arrival_date?: string;
	departure_date?: string;
	persons_count?: string;
	amount_of_rooms?: number;
	checkout_type?: string;
	ga_origin?: string;
	nights?: number;
	ga_linker_code?: string;
	product_filters?: number[];
	persons?: string[];
	read_only?: boolean;
};

let stopWatcher: () => void;

/**
 * Cache key (arrival|departure|product|persons|rooms) of the combo for which the expensive
 * upsell groups were last loaded, so re-entering the choice steps for the same combo is a no-op.
 */
let loadedUpsellsKey: string | null = null;

/***************************************************************************************
 *                                                                                     *
 * ⛔️ STOP! DON'T JUST ADD ANYTHING, KEEP IN MIND EVERY UPDATE RESULTS IN AN API CALL. *
 *                                                                                     *
 ***************************************************************************************/
export const useFlowStore = defineStore('flow', {
	state: (): FlowStoreState => ({
		/* Same as checkout.checkout_id, but persisted, used to retrieve checkout data from the server */
		id: persistResumable('flow/id', ''),

		/* Checkout does always exist since it will be preloaded in App.vue */
		checkout: {} as CheckoutResource,

		/* True once the guest deliberately changed the number of rooms (#4737). */
		roomsTouchedByUser: false,
	}),

	getters: {
		isArrangement(state): boolean {
			return (
				state.checkout?.meta?.product?.entity === 'arrangement' || state.checkout?.meta?.product.type === 'arrangement'
			);
		},

		isHotelFlow(state) {
			return state.checkout?.meta?.product?.type === 'hotel';
		},

		hotelCategoryChoices(state): HotelRoomCategoryResource[] {
			const roomProducts = state.checkout.meta.arrangement_choices?.room_products;

			if (roomProducts === undefined) {
				return [];
			}

			return roomProducts
				.map((product) => {
					if (!('products' in product)) {
						return null;
					}

					const result = hotelRoomCategoryResource.safeParse(product);

					if (!result.success) {
						return null;
					}

					return result.data;
				})
				.filter((p) => p !== null);
		},

		roomChoices(state): UpsellProductResource[] {
			const roomProducts = state.checkout.meta.arrangement_choices?.room_products;

			if (roomProducts === undefined) {
				return [];
			}

			return roomProducts
				.map((product) => {
					if ('products' in product) {
						return null;
					}

					const result = upsellProductResource.safeParse(product);

					if (!result.success) {
						return null;
					}

					const room = result.data;

					if (!room.is_available && room.hide_when_not_available) {
						return null;
					}

					return room;
				})
				.filter((p) => p !== null);
		},

		startDate(state) {
			const startDateString = state.checkout?.data?.arrival?.date ?? new Date().toDateString();

			return formatDate(startDateString);
		},

		endDate(state) {
			const dateString = state.checkout?.data?.departure?.date;

			return dateString ? formatDate(dateString) : null;
		},

		hasTreatmentOrRitual(state): boolean {
			return checkoutHasTreatmentOrRitual(state.checkout);
		},

		hasReceiptUpsellsAvailable(state) {
			if (state.checkout?.meta?.upsells?.receipt) {
				return !state.checkout.meta.upsells.receipt.products.every(
					(product) => product.persons?.every((person) => person.selected) || !product.is_available,
				);
			}
			return false;
		},

		checkoutTypes(state) {
			if (state.checkout?.meta?.supported_checkout_types) {
				return state.checkout?.meta?.supported_checkout_types;
			}
			return [];
		},

		entreeProducts(state) {
			return Object.entries(state.checkout?.meta?.entree_product_ids ?? {})
				.map(([key, product_id]) => ({ key, product_id }))
				.filter((item): item is { key: string; product_id: number } => item.product_id !== null);
		},
	},
	actions: {
		resetFlow() {
			this.id = '';
			this.roomsTouchedByUser = false;
			loadedUpsellsKey = null;
			useResumableReservation().forget();
		},

		/**
		 * Store the checkout cart data on the server.
		 */
		async save(noPatching: boolean = false, includeUpsells: boolean = false): Promise<void> {
			const loadingStore = useLoadingStore();
			const validationStore = useValidationStore();
			const transaction = useTransaction();

			// In case of resetting the flow, ex. changing the checkout type.
			// the id is reset to and empty string and we don't want to store this. So we resolve directly.
			if (this.id === '') return Promise.resolve();

			const result = checkoutResource.safeParse(this.$state.checkout);

			if (result.success) {
				const loaders = noPatching ? [] : loadingStore.loading('patching');

				try {
					const checkoutData = await api.checkout.updateCheckout(this.id, this.$state.checkout, includeUpsells);

					pushSessionExpiry();

					if (!noPatching) {
						this.$patch((state) => syncCheckout(state.checkout, checkoutData, loadedUpsellsKey));
					}

					transaction.patch(); // Register that there was a patch.
				} catch (error) {
					// Only throw exceptions when the exception is not an cancel request.
					if (!isCancel(error)) {
						// Without this every further mutation repeats the same failure.
						if (isCartNotFoundError(error)) this.resetFlow();

						if (isAxiosError(error) && error.response?.status) {
							validationStore.setErrors({
								checkout: [i18n.global.t('unfortunately_an_error_has_occurred_please_try_again_shortly')],
							});
						}

						throw error;
					}
				}

				loadingStore.unloading(...loaders);
			} else {
				console.error('schema validation failed');
			}
		},

		/**
		 * Load an existing or new checkout with cart data from the server into the flow state.
		 */
		async load(options: CheckoutOptions = {}): Promise<void> {
			const requestedId = options.checkout ? options.checkout : this.id;

			const checkoutData = await api.checkout.createCheckout({
				amount_of_rooms: options.amount_of_rooms,
				arrival: options.arrival_date ? { date: options.arrival_date } : undefined,
				bath_card_id: options.bath_card_id,
				checkout_id: options.checkout ? options.checkout : this.id,
				checkout_type: options.checkout_type,
				departure: options.departure_date ? { date: options.departure_date } : undefined,
				ga_linker_code: options.ga_linker_code,
				ga_origin: options.ga_origin,
				options: options.read_only
					? {
							read_only: options.read_only,
						}
					: undefined,
				persons_count: options.persons_count,
				nights: options.nights,
				product_filters: options.product_filters,
				product_id: options.product,
				voucher_codes: options.voucher_codes,
			});

			this.$patch((state) => {
				state.id = checkoutData.data.checkout_id;
				syncCheckout(state.checkout, checkoutData, loadedUpsellsKey);
			});

			// This endpoint is an upsert: an id whose cart is gone yields a fresh checkout
			// instead of an error, so that reservation must never be offered as resumable again.
			if (requestedId !== '' && checkoutData.data.checkout_id !== requestedId) {
				useResumableReservation().forget();
			}
		},

		/**
		 * Lazy-load the expensive upsell groups (treatments, rituals, hotel rooms, lunch & diner).
		 *
		 * Initial load and date/time switches skip these groups for performance, so they are
		 * fetched here when the user enters the choice steps. Cached per date/product/persons
		 * combo so re-entering the same combo does not refetch; a changed combo refetches and
		 * overwrites the now-stale upsells.
		 */
		async loadUpsells(): Promise<void> {
			if (this.id === '') return;

			const key = upsellsKey(this.checkout);

			if (loadedUpsellsKey === key && this.checkout?.meta?.upsells?.treatments) return;

			const loadingStore = useLoadingStore();
			const loaders = loadingStore.loading('validating_step');

			try {
				const checkoutData = await api.checkout.getCheckout(this.id);
				const result = checkoutResource.safeParse(checkoutData);

				if (!result.success) {
					throw result.error;
				}

				// Hydrate everything the choice steps depend on, not just the upsell groups: the hotel
				// room products live under arrangement_choices and are likewise omitted from the cheap
				// (include_upsells=0) responses. Without them the mandatory room step drops out of the
				// navigation and the guest is routed past it (#4802).
				this.$patch({
					checkout: {
						meta: {
							upsells: result.data.meta.upsells,
							arrangement_choices: result.data.meta.arrangement_choices,
						},
					},
				});
				loadedUpsellsKey = key;
			} catch (error) {
				// A superseded request is not a real failure; rethrow everything else so the
				// caller can show a retry instead of rendering a choice step without its data.
				if (!isCancel(error)) throw error;
			} finally {
				loadingStore.unloading(...loaders);
			}
		},

		/**
		 * Change the main product of the flow.
		 */
		async changeMainProduct(product_id: number) {
			const loadingStore = useLoadingStore();
			const loaders = loadingStore.loading('changing_main_product');
			this.$patch({ checkout: { data: { product_id } } });
			await this.save();
			loadingStore.unloading(...loaders);
		},

		/**
		 * Change the checkout date of the reservation
		 */
		async setCheckoutDate(arrivalDate: string, departureDate: string | null) {
			const loadingStore = useLoadingStore();
			const loaders = loadingStore.loading('changing_date');
			const data = {
				arrival: { date: arrivalDate },
				departure: { date: departureDate },
			};

			// BUGFIX: For some reason pinia does not patch/remove record keys correctly
			// that's why notices is set here explicit to undefined.
			this.$patch({
				checkout: {
					data,
					notices: undefined,
					calendar_notifications: undefined,
					calendar_notifications_above: undefined,
				},
			});

			await this.save();

			loadingStore.unloading(...loaders);
		},

		/**
		 * Set checkout time, will be stored on the server but the result of the api
		 * is not patched back into the store, to be non-intrusive.
		 */
		async setCheckoutTime(type: 'arrival' | 'departure', time: string | null) {
			this.$patch({ checkout: { data: { [type]: { time } } } });
			await this.save();
		},

		/**
		 * Set both checkout time, will be stored on the server but the result of the api
		 * is not patched back into the store, to be non-intrusive.
		 */
		async setCheckoutTimes(arrivalTime: string | null, departureTime: string | null) {
			this.$patch({
				checkout: {
					data: {
						arrival: { time: arrivalTime },
						departure: { time: departureTime },
					},
				},
			});
			await this.save();
		},

		/**
		 * Sets the persons count and optimistic removes the person from the store,
		 * saves the state and patches the data back.
		 */
		async setPersonsCount(persons_count: number) {
			const loadingStore = useLoadingStore();

			// Only show the loader when incrementing number of persons.
			const loaders =
				persons_count <= this.checkout.data.persons_count ? [] : loadingStore.loading('changing_person_choice');

			this.$patch({
				checkout: {
					data: {
						persons_count,
						...(this.roomsTouchedByUser ? {} : { amount_of_rooms: 1 }),
					},
					meta: {
						receipt: {
							persons: this.checkout.meta.receipt.persons.slice(0, persons_count),
						},
					},
				},
			});

			await this.save();
			loadingStore.unloading(...loaders);
		},

		/**
		 * Sets the number of rooms, marks it as a deliberate guest choice so it is no longer
		 * auto-overridden, saves the state and patches the data back.
		 */
		async setRoomsCount(amount_of_rooms: number) {
			const loadingStore = useLoadingStore();

			// Only show the loader when incrementing number of persons.
			const loaders = loadingStore.loading('changing_person_choice');

			this.roomsTouchedByUser = true;

			this.$patch({
				checkout: {
					data: { amount_of_rooms },
				},
			});

			await this.save();
			loadingStore.unloading(...loaders);
		},

		/**
		 * Sets the persons count and optimistic removes the person from the store,
		 * saves the state and patches the data back.
		 */
		async setProductFilters(product_filters: number[]) {
			const loadingStore = useLoadingStore();

			// Only show the loader when incrementing number of persons.
			const loaders = loadingStore.loading('changing_product_filters');

			this.$patch({
				checkout: {
					data: { product_filters },
				},
			});

			await this.save();
			loadingStore.unloading(...loaders);
		},

		/**
		 * Update a person product choice.
		 */
		async setPersonChoice(personId: number, slot: string, productId: number): Promise<void> {
			const loadingStore = useLoadingStore();
			const transaction = useTransaction();

			const loaders = loadingStore.loading('patching', 'changing_person_choice');

			const checkoutData = await api.checkout.setPersonChoice(this.id, personId, slot, productId);

			pushSessionExpiry();
			const result = checkoutResource.safeParse(checkoutData);

			if (result.success) {
				this.$patch((state) => syncCheckout(state.checkout, checkoutData, loadedUpsellsKey));
				transaction.patch();
			} else {
				console.error('meta validation failed');
			}

			loadingStore.unloading(...loaders);
		},

		/**
		 * Update a person product choice.
		 */
		async setPersonChoices(personIds: number[], slot: string, productId: number): Promise<void> {
			const loadingStore = useLoadingStore();
			const transaction = useTransaction();

			const loaders = loadingStore.loading('patching', 'changing_person_choice');

			const checkoutData = await api.checkout.setPersonChoices(this.id, personIds, slot, productId);

			pushSessionExpiry();
			const result = checkoutResource.safeParse(checkoutData);

			if (result.success) {
				this.$patch((state) => syncCheckout(state.checkout, checkoutData, loadedUpsellsKey));
				transaction.patch();
			} else {
				console.error('meta validation failed');
			}

			loadingStore.unloading(...loaders);
		},

		/**
		 * Delete a person product choice
		 */
		async deletePersonChoice(personId: number, slot: string, productId: number): Promise<void> {
			const loadingStore = useLoadingStore();
			const transaction = useTransaction();

			const loaders = loadingStore.loading('patching');

			const checkoutData = await api.checkout.deletePersonChoice(this.id, personId, slot, productId);

			pushSessionExpiry();
			const result = checkoutResource.safeParse(checkoutData);

			if (result.success) {
				this.$patch((state) => syncCheckout(state.checkout, checkoutData, loadedUpsellsKey));
				transaction.patch();
			} else {
				console.error('meta validation failed');
			}

			loadingStore.unloading(...loaders);
		},

		/**
		 * Create a booking
		 */
		async createBooking(): Promise<BookingResource> {
			const validationStore = useValidationStore();
			const loadingStore = useLoadingStore();
			const personalStore = useCustomerStore();
			const cookie = useCookie();

			const loaders = loadingStore.loading('validating_booking');

			try {
				if (bridge.isIntergrated) {
					bridge.receiveMessages('reservation-flow', {
						'reservation-flow.linkercode': (data) => {
							console.log('received linkercode.. in flow!', data);
							if (data?._gl) {
								this.$patch({
									checkout: {
										data: { ga_linker_code: data?._gl },
									},
								});
							}
						},
					});

					bridge.sendMessage('reservation-flow', 'reservation-flow.linkercode');

					// Wait 50ms to retrieve the linker code from the website
					await new Promise((f) => setTimeout(f, 50));
				}

				await this.save(); // Save the current checkout

				if (personalStore.authentication.action === 'login' && personalStore.authentication.access_token) {
					api.client.setAccessToken(personalStore.authentication.access_token);
				} else {
					api.client.delAccessToken();
				}

				let token_cookie = null;
				if (Cookies.get('_ga')) {
					token_cookie = Cookies.get('_ga')?.substring(6) || null;
				}

				return await api.checkout.createBooking(this.id, {
					personal_data: personalStore.personal,
					terms: personalStore.terms,
					comment: personalStore.comment,
					authentication: personalStore.authentication,
					user_agent: window.navigator.userAgent,
					screen_resolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
					browser_language: navigator.language,
					token_cookie: token_cookie,
					session_cookie: cookie.getSessionCookie(config.theme),
					global_session_cookie: cookie.getSessionCookie('global'),
				});
			} catch (error) {
				if (isAxiosError(error) && error.response?.data?.errors) {
					validationStore.setErrors(error.response.data.errors);
				}

				if (isAxiosError(error) && error.response?.status === 401) {
					validationStore.setErrors({ authentication: [error.response?.data.message] });
					api.client.delAccessToken();
				}

				throw error;
			} finally {
				loadingStore.unloading(...loaders);
			}
		},

		/**
		 * Validate the checkout for a given step
		 * @returns boolean true when no errors accur.
		 */
		async validateStep(step: string | undefined) {
			const validationStore = useValidationStore();
			const loadingStore = useLoadingStore();

			validationStore.clearErrors();

			// Skip when step is undefined.
			if (!step) return true;

			const loaders = loadingStore.loading('validating_step');

			try {
				await api.checkout.validateCheckoutStep(this.id, step);
				await this.save();

				if (step === 'reservation') useResumableReservation().markResumable();

				return true;
			} catch (error) {
				if (isCartNotFoundError(error)) this.resetFlow();

				if (isAxiosError(error) && error.response?.data?.errors) {
					validationStore.setErrors(error.response.data.errors);
				}

				throw error;
			} finally {
				loadingStore.unloading(...loaders);
			}
		},

		/**
		 * Watch for changes
		 */
		watchAndSaveChanges() {
			if (stopWatcher) stopWatcher();
			stopWatcher = this.$subscribe((mutation) => {
				if (mutation.type === 'direct') {
					this.save();
				}
			});
		},
	},
});

// if (import.meta.hot) {
//   import.meta.hot.accept(acceptHMRUpdate(useFlowStore, import.meta.hot));
// }
